import AutoFixHighIcon from "@mui/icons-material/AutoFixHighRounded";
import { Alert, Button, Stack, Typography } from "@mui/material";
import ArtifactAgentNote from "./ArtifactAgentNote";
import ArtifactAgentProgress from "./ArtifactAgentProgress";
import ArtifactAgentReply from "./ArtifactAgentReply";
import ArtifactAgentSavedReply from "./ArtifactAgentSavedReply";
import ArtifactAgentUserMessage from "./ArtifactAgentUserMessage";
import type { AgentFixRequest, AgentQuestion, AgentTurn } from "./agentChatState";

const describeImageCount = (count: number): string => `${count} ${count === 1 ? "image" : "images"}`;

interface ClarificationReplyProps {
  clarification: AgentQuestion;
  imageCount: number;
  isAnswerable: boolean;
  disabled: boolean;
  onAnswer: (answer: string) => void;
}

const ClarificationReply = ({
  clarification,
  imageCount,
  isAnswerable,
  disabled,
  onAnswer,
}: ClarificationReplyProps) => (
  <>
    <Typography sx={{ whiteSpace: "pre-wrap" }}>{clarification.question}</Typography>
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
      {clarification.options.map((option, index) => (
        <Button
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          variant="outlined"
          color="secondary"
          disabled={disabled || !isAnswerable}
          onClick={() => onAnswer(option)}
        >
          {option}
        </Button>
      ))}
    </Stack>
    {isAnswerable && imageCount > 0 && (
      <Typography variant="caption" color="textSecondary">
        {describeImageCount(imageCount)} will be sent with your answer.
      </Typography>
    )}
  </>
);

interface Props {
  turn: AgentTurn;
  savedAt?: string;
  isAnswerable: boolean;
  disabled: boolean;
  onAnswer: (answer: string) => void;
  onFix: (fix: AgentFixRequest) => void;
  onReload: () => void;
}

const ArtifactAgentTurn = ({ turn, savedAt, isAnswerable, disabled, onAnswer, onFix, onReload }: Props) => {
  const { result, fixOffer } = turn;
  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
        <ArtifactAgentUserMessage content={turn.prompt} />
        {turn.imageCount > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ px: 0.5 }}>
            {describeImageCount(turn.imageCount)}
          </Typography>
        )}
      </Stack>
      <ArtifactAgentReply>
        <ArtifactAgentProgress
          progress={turn.progress}
          status={result.status}
          startedAt={turn.startedAt}
          endedAt={turn.endedAt}
        />
        {result.status === "saved" && (
          <ArtifactAgentSavedReply
            summary={result.summary}
            notCopied={result.notCopied}
            version={result.version}
            savedAt={savedAt}
          />
        )}
        {turn.notes.map((note, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <ArtifactAgentNote key={index} severity={note.severity} title={note.title} lines={note.lines} />
        ))}
        {result.status === "clarification" && (
          <ClarificationReply
            clarification={result.clarification}
            imageCount={turn.compare ? 0 : turn.imageCount}
            isAnswerable={isAnswerable}
            disabled={disabled}
            onAnswer={onAnswer}
          />
        )}
        {result.status === "stopped" && (
          <Typography variant="caption" color="textSecondary">
            Stopped
          </Typography>
        )}
        {result.status === "failed" && (
          <Alert
            severity="error"
            action={
              result.offerReload && (
                <Button color="inherit" disabled={disabled} onClick={onReload}>
                  Reload
                </Button>
              )
            }
          >
            {result.message}
          </Alert>
        )}
        {turn.compareSkipLine && (
          <Typography variant="caption" color="textSecondary">
            {turn.compareSkipLine}
          </Typography>
        )}
        {fixOffer && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AutoFixHighIcon />}
            disabled={disabled}
            onClick={() => onFix(fixOffer)}
            sx={{ alignSelf: "flex-start" }}
          >
            Ask the agent to fix
          </Button>
        )}
      </ArtifactAgentReply>
    </Stack>
  );
};

export default ArtifactAgentTurn;
